import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { EMPTY, forkJoin, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { Options } from '@ph-core/enums/options.enum';
import { Vendor } from '@ph-core/enums/vendor.enum';
import { ConfigService } from '@ph-core/services/config/config.service';
import { EContractApiService } from '@ph-core/services/econtract-api.service';
import { ContractType, ErroredContractResponse } from '@ph-model/api';
import { PendingContractResponse } from '@ph-model/api/response/pending-contract.response.model';
import { SubmitCartResponse } from '@ph-model/checkout/submit_cart/response/submit-cart.response.model';
import { SubmittedCartItem } from '@ph-model/checkout/submit_cart/response/submitted-cart-item.model';
import { EContractStatusType } from '@ph-model/e-contract-status-type';
import { UserResponse } from '@ph-model/login';
import { SaveAncillaryEContractRequest } from '@ph-model/request/save-ancillary-econtract';
import {
  GeneratePDFEContractRequest,
  SavePowerUpEContractRequest,
  UpdateStatusEContractRequest,
} from '@ph-model/request/save-power-up-econtract';
import { SubmitCartRequest } from '@ph-model/request/submit-cart.request';
import { SubmitPendingContractsRequest } from '@ph-model/request/submit-pending-contracts.request';
import { UndoDeclineContractsRequest } from '@ph-model/request/undo-decline-contracts.request';
import {
  EContractErrorResponse,
  EContractSuccessResponse,
  GeneratePDFEContractResponse,
  StandardEContractResponse,
} from '@ph-model/response/e-contract.response';
import { RemitService } from '@ph-remit/services/remit.service';
import { IComplexDate } from '@ph-shared/utils/date-control-formater';
import { convertToCompatibleDateFormat } from '@ph-shared/utils/date.util';
import { openPDFInWindow } from '@ph-shared/utils/open-pdf';
import { CartItem } from '@ph-shopping/model/cart';
import { SubmitCartModel } from '@ph-shopping/model/cart/submit-cart.model';
import { VehicleState, VehicleValues } from '@ph-shopping/store/vehicle/vehicle.store';
import { SupportedLanguageCodes } from '@ph-static-data/languages';
import { tireCoverageCodesAllowedInSameCart } from '@ph-static-data/tire-product-codes';
import { UserStore, UserStoreType } from '@ph-store/user/user.store';

export interface ContractData {
  econContractNumber: string;
  contractSource: string | undefined;
  vendor: string | null | undefined;
}

export interface SubmitCartResult {
  invalidCart: CartItem[];
  submittedCart: SubmittedCartItem[];
}

@Injectable({ providedIn: 'root' })
export class ContractSaveFacade {
  private httpClient = inject(HttpClient);
  private configService = inject(ConfigService);
  private econtractApiService = inject(EContractApiService);
  private remitService = inject(RemitService);
  private userStore: UserStoreType = inject(UserStore);

  submitCart(
    payload: SubmitCartModel | SubmitCartRequest,
    vehicleState: VehicleState,
    user: UserResponse
  ): Observable<SubmitCartResult> {
    const useNewSaveContractApi = this.configService.environment.features.shared.useNewSaveContractApi;

    return useNewSaveContractApi
      ? this._submitCartWithNewRatesApi(payload as SubmitCartModel, vehicleState, user)
      : this._submitCartLegacy(payload as SubmitCartRequest, vehicleState.values, user);
  }

  submitPendingRemit(
    pendingToRemitContracts: ContractType[],
    pendingToDeclineContracts: ContractType[],
    user: UserResponse
  ): Observable<PendingContractResponse> {
    const useNewSaveContractApi = this.configService.environment.features.shared.useNewSaveContractApi;

    return useNewSaveContractApi
      ? this._submitPendingRemitWithNewRatesApi(pendingToRemitContracts, pendingToDeclineContracts, user)
      : this._submitPendingRemitLegacy(pendingToRemitContracts, pendingToDeclineContracts, user);
  }

  submitContractDeclines(
    contractsToDecline: ContractType[],
    user: UserResponse
  ): Observable<ErroredContractResponse> {
    const useNewSaveContractApi = this.configService.environment.features.shared.useNewSaveContractApi;

    return useNewSaveContractApi
      ? this._submitContractDeclinesWithNewRatesApi(contractsToDecline)
      : this._submitContractDeclinesLegacy(contractsToDecline, user);
  }

  undoDecline(contractType: ContractType): Observable<void> {
    const useNewSaveContractApi = this.configService.environment.features.shared.useNewSaveContractApi;

    if (useNewSaveContractApi) {
      const request = new UpdateStatusEContractRequest(contractType.econContractNumber, EContractStatusType.Pending);

      return this.econtractApiService.updateStatusEContract(request).pipe(map(() => undefined));
    }

    const user = this.userStore.user();
    if (!user) {
      console.error('ContractSaveFacade: undoDecline: User is not logged in');

      return EMPTY;
    }

    const allDealers = user.allDealers ? JSON.parse(JSON.stringify(user.allDealers)) : [];
    if (allDealers && allDealers.length === 1) {
      allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
    }

    const request: UndoDeclineContractsRequest = {
      cmsDealerNumber: user.cmsDealerNumber,
      programId: user.programId,
      allDealers,
      contract: contractType,
    };

    return this.httpClient
      .post<PendingContractResponse>(`${this.configService.environment.apiUrl}/undodecline`, request)
      .pipe(map(() => undefined));
  }

  generatePdf(contractData: ContractData, language?: SupportedLanguageCodes): Observable<void> {
    const environment = this.configService.environment;
    if (environment.features.shared.useNewSaveContractApi) {
      return this.econtractApiService
        .generatePDFEContract(new GeneratePDFEContractRequest(contractData.econContractNumber, language))
        .pipe(
          tap((data: GeneratePDFEContractResponse) => openPDFInWindow(data.pdf)),
          map(() => undefined)
        );
    }

    const dealerNumber = this.userStore.user()?.cmsDealerNumber;
    const payload = {
      contractNumber: contractData.econContractNumber,
      vendor: contractData.vendor,
      contractSource: contractData.contractSource,
      dealerNumber,
      ...(language && { language }),
    };

    return this.httpClient
      .post(`${environment.apiUrl}/generatePdf`, { ...payload, pdfFormat: 'BINARY' }, { responseType: 'blob' })
      .pipe(
        map((res: Blob) => new Blob([res], { type: 'application/pdf' })),
        tap((data: Blob) => window.open(URL.createObjectURL(data))),
        map(() => undefined)
      );
  }

  private _submitCartLegacy(
    cartRequest: SubmitCartRequest,
    vehicleValues: VehicleValues,
    user: UserResponse
  ): Observable<SubmitCartResult> {
    const environment = this.configService.environment;
    const locale = cartRequest?.locale;
    delete cartRequest?.locale;

    cartRequest.dealer = user.cmsDealerNumber;
    cartRequest.request = {
      ...vehicleValues,
      model:
        vehicleValues.other && vehicleValues.other.toLowerCase() !== 'other'
          ? vehicleValues.other
          : vehicleValues.model,
      vin: vehicleValues.vin || vehicleValues.hin || vehicleValues.pid || vehicleValues.tin,
      DOBBorrower: convertToCompatibleDateFormat(vehicleValues.DOBBorrower) as IComplexDate,
      DOBCoBorrower: convertToCompatibleDateFormat(vehicleValues.DOBCoBorrower) as IComplexDate,
      saleDate: convertToCompatibleDateFormat(vehicleValues.saleDate) as IComplexDate,
      inServiceDate: convertToCompatibleDateFormat(vehicleValues.inServiceDate) as IComplexDate,
      purchaseDate: convertToCompatibleDateFormat(vehicleValues.purchaseDate) as IComplexDate,
      customerTaxExempt: (vehicleValues.customerTaxExempt
        ? vehicleValues.customerTaxExempt === Options.YES
        : null) as unknown as Options,
    };
    cartRequest.programId = user.programId;
    cartRequest.vehicleForm = {
      msrp: vehicleValues.msrp,
      grossCapCost: vehicleValues.grossCapCost,
      apr: vehicleValues.apr,
      financeAmount: vehicleValues.financeAmount,
      registrationNumber: vehicleValues.registrationNumber,
      purchasePrice: vehicleValues.purchasePrice,
    };
    cartRequest.applicationName = user.applicationName;
    cartRequest.userName = user.userName;

    if (locale) {
      // needs to be removed in http interceptor
      cartRequest['locale-to-be-removed'] = locale;
    }

    return this.httpClient.post<SubmitCartResponse>(`${environment.apiUrl}/savecontract`, cartRequest);
  }

  private _submitCartWithNewRatesApi(
    submitCart: SubmitCartModel,
    vehicleState: VehicleState,
    user: UserResponse
  ): Observable<SubmitCartResult> {
    const environment = this.configService.environment;
    const requests: Observable<EContractSuccessResponse | EContractErrorResponse>[] = [];
    let indexOfTireContract: number | null = null;

    submitCart.cart.forEach((cartItem: CartItem) => {
      if (cartItem.product.productCode === 'GMWC') {
        const request = SavePowerUpEContractRequest.generateRequest(
          submitCart,
          cartItem,
          vehicleState,
          user.cmsDealerNumber,
          user.dealerInfo
        );
        requests.push(this.econtractApiService.savePowerUpEContract(request));
      } else {
        let isTireProduct = false;
        if (environment.vendorName === Vendor.GM) {
          user.dealerInfo.agentCode = cartItem.product.programAgentCode;
        }
        if (tireCoverageCodesAllowedInSameCart.includes(cartItem.product.coverageCode)) {
          indexOfTireContract = indexOfTireContract === null ? 0 : indexOfTireContract + 1;
          isTireProduct = true;
        }

        const request = SaveAncillaryEContractRequest.generateRequest(
          submitCart,
          cartItem,
          vehicleState,
          user.cmsDealerNumber,
          user.dealerInfo,
          isTireProduct ? indexOfTireContract : null
        );
        requests.push(this.econtractApiService.saveAncillaryEContract(request));
      }
    });

    return forkJoin([...requests]).pipe(
      map((response: (EContractSuccessResponse | EContractErrorResponse)[]) => {
        const invalidCart: CartItem[] = [];
        const submittedCart: SubmittedCartItem[] = [];
        const contractsPDFToOpen: string[] = [];

        response.forEach((res: EContractSuccessResponse | EContractErrorResponse, index: number) => {
          if ((res as EContractSuccessResponse)?.standardResponse?.status === 'SUCCESS') {
            const cart = new SubmittedCartItem(
              submitCart.cart[index].product,
              submitCart.cart[index].key,
              true,
              submitCart.cart[index].message,
              '', //code
              (res as EContractSuccessResponse).contractId,
              '', // contractSource
              submitCart.cart[index].errorType ?? []
            );
            contractsPDFToOpen.push((res as EContractSuccessResponse)?.pdf);
            submittedCart.push(cart);
          } else {
            const cartItem: CartItem = {
              ...submitCart.cart[index],
              valid: false,
              message: [
                ...(submitCart.cart[index].message?.flat() || []),
                (res as EContractErrorResponse).responseDescription,
              ],
              errorType: [...(submitCart.cart[index].errorType?.flat() || []), 'CREATE-CONTRACT-FAILURE'],
            };
            invalidCart.push(cartItem);
          }
        });

        if (contractsPDFToOpen.length) {
          contractsPDFToOpen.forEach((pdf: string) => openPDFInWindow(pdf));
        }

        return { invalidCart, submittedCart };
      })
    );
  }

  private _submitContractDeclinesLegacy(
    contractsToDecline: ContractType[],
    user: UserResponse
  ): Observable<ErroredContractResponse> {
    const request: SubmitPendingContractsRequest = {
      cmsdealernumber: user.cmsDealerNumber,
      pendingToRemitContracts: [],
      pendingToDeclineContracts: contractsToDecline,
      programId: user.programId,
      allDealers: user.allDealers,
      pendingDays: user.pendingDays,
    };

    return this.httpClient
      .post<PendingContractResponse>(`${this.configService.environment.apiUrl}/remitcontracts`, request)
      .pipe(
        map(
          (response) =>
            response.erroredContracts ?? {
              declinedErrorContracts: [],
              remittedErrorContracts: [],
            }
        )
      );
  }

  private _submitContractDeclinesWithNewRatesApi(
    contractsToDecline: ContractType[]
  ): Observable<ErroredContractResponse> {
    const requests = contractsToDecline.map((contract: ContractType) =>
      this.econtractApiService.updateStatusEContract(
        new UpdateStatusEContractRequest(contract.econContractNumber, EContractStatusType.Decline)
      )
    );

    return forkJoin(requests).pipe(
      map((response: StandardEContractResponse[]) => {
        const erroredContracts: ErroredContractResponse = {
          declinedErrorContracts: [],
          remittedErrorContracts: [],
        };

        response.forEach((res: StandardEContractResponse, index: number) => {
          if (res?.status && res?.status !== 'SUCCESS') {
            const erroredContract = contractsToDecline[index];
            erroredContract.standardResponse = { ...res };
            erroredContracts.declinedErrorContracts.push(erroredContract);
          }
        });

        return erroredContracts;
      })
    );
  }

  private _submitPendingRemitLegacy(
    pendingToRemitContracts: ContractType[],
    pendingToDeclineContracts: ContractType[],
    user: UserResponse
  ): Observable<PendingContractResponse> {
    const environment = this.configService.environment;
    const request: SubmitPendingContractsRequest = {
      cmsdealernumber: user.cmsDealerNumber,
      pendingToRemitContracts,
      pendingToDeclineContracts,
      programId: user.programId,
      allDealers: user.allDealers,
      pendingDays: user.pendingDays,
    };

    return this.httpClient.post<PendingContractResponse>(`${environment.apiUrl}/remitcontracts`, request).pipe(
      switchMap((remitResponse) => {
        const allDealers = user.allDealers ? JSON.parse(JSON.stringify(user.allDealers)) : [];
        if (allDealers && allDealers.length === 1) {
          allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
        }

        return this.httpClient
          .post<PendingContractResponse>(`${environment.apiUrl}/getpendingcontracts`, {
            cmsdealernumber: user.cmsDealerNumber,
            programId: user.programId,
            pendingDays: user.pendingDays,
            allDealers,
            pendingToRemitContracts: [],
            pendingToDeclineContracts: [],
          })
          .pipe(
            map((refreshResponse) => {
              if (environment.features.suppressDealerCost) {
                this.remitService.suppressDealerCost(refreshResponse.pendingContracts);
              }

              return {
                ...refreshResponse,
                erroredContracts: remitResponse.erroredContracts ?? {
                  declinedErrorContracts: [],
                  remittedErrorContracts: [],
                },
              };
            })
          );
      })
    );
  }

  private _submitPendingRemitWithNewRatesApi(
    pendingToRemitContracts: ContractType[],
    pendingToDeclineContracts: ContractType[],
    user: UserResponse
  ): Observable<PendingContractResponse> {
    const environment = this.configService.environment;

    const pendingToRemitContractsRequests = pendingToRemitContracts.map((contract: ContractType) =>
      this.econtractApiService.updateStatusEContract(
        new UpdateStatusEContractRequest(contract.econContractNumber, EContractStatusType.Remit)
      )
    );

    const pendingToDeclineContractsRequests = pendingToDeclineContracts.map((contract: ContractType) =>
      this.econtractApiService.updateStatusEContract(
        new UpdateStatusEContractRequest(contract.econContractNumber, EContractStatusType.Decline)
      )
    );

    return forkJoin([...pendingToRemitContractsRequests, ...pendingToDeclineContractsRequests]).pipe(
      switchMap((response: StandardEContractResponse[]) => {
        const allDealers = JSON.parse(JSON.stringify(user.allDealers));
        if (allDealers && allDealers.length === 1) {
          allDealers[0].cmsDealerNumber = user.cmsDealerNumber;
        }

        const erroredContracts: ErroredContractResponse = {
          declinedErrorContracts: [],
          remittedErrorContracts: [],
        };

        const allSubmittedContracts = [...pendingToRemitContracts, ...pendingToDeclineContracts];

        response.forEach((res: StandardEContractResponse, index: number) => {
          if (res?.status && res?.status !== 'SUCCESS') {
            const erroredContract = allSubmittedContracts[index];
            erroredContract.standardResponse = { ...res };

            if (index < pendingToRemitContracts.length) {
              erroredContracts.remittedErrorContracts.push(erroredContract);
            } else {
              erroredContracts.declinedErrorContracts.push(erroredContract);
            }
          }
        });

        const { cmsDealerNumber, programId, pendingDays } = user;

        return this.httpClient
          .post<PendingContractResponse>(`${environment.apiUrl}/getpendingcontracts`, {
            cmsdealernumber: cmsDealerNumber,
            programId,
            pendingDays,
            allDealers,
            pendingToRemitContracts: [],
            pendingToDeclineContracts: [],
          })
          .pipe(
            map((refreshResponse) => {
              if (environment.features.suppressDealerCost) {
                this.remitService.suppressDealerCost(refreshResponse.pendingContracts);
              }

              return { ...refreshResponse, erroredContracts };
            })
          );
      })
    );
  }
}
